import { Icon, StatusPill } from '../common';
import { deskPayAction, derivePaymentStatus } from '../bookings/domain';
import { fmtEuro } from '../../lib/money';
import type { Booking, BookingStatus } from '../bookings/types';

interface Props {
  b: Booking;
  // The status to show on the badge — the desk-stage status (a collection reads
  // "Arrived" until collected), which may differ from b.status. See DeskRow.
  status: BookingStatus;
  // Advance the booking's status (drop-off: → Arrived; collection: → Collected;
  // click again to undo).
  onAction: () => void;
  // THE ACTION IS DONE for this desk — the dog has arrived (drop-offs) or gone
  // (collections). The desk's pill is one colour while there is something to
  // do and grey once it is done, whatever the status underneath says, and only
  // the row knows which side it is on (Freddi, 2026-10-06).
  done?: boolean;
  onTogglePaid: () => void;
  // Disable each control while its own optimistic write is in flight — prevents
  // a second press racing the first into an out-of-order server result.
  busyStatus?: boolean;
  busyPaid?: boolean;
  /** Opens the booking — what a request's pill does instead of advancing it (R-DESK-11). */
  onOpen?: () => void;
}

// The desk row's right-hand cluster: the payment control + the status badge. The
// payment is a quiet control that matches the row until hover (mark-paid toggle
// on every row). The status is the shared StatusPill — the SAME icon/label/tone
// as the bookings table, showing the actual status and advancing it on click, so
// Daily speaks the same status language as everywhere else.
export function DeskActions({ b, status, onAction, onTogglePaid, busyStatus, busyPaid, done = false, onOpen }: Props) {
  // A REQUEST HAS NOTHING TO PAY AND NOTHING TO ADVANCE (R-DESK-11, request-a-
  // stay `sd`). No pay button — nothing is due until it is accepted — and the
  // pill opens the request rather than moving it: advancing would have taken a
  // request straight to Arrived, past the yes and past the size gate. The
  // decision itself is `accept-or-decline`'s.
  if (b.status === 'requested') {
    return (
      <div className="desk-actions">
        <StatusPill
          status="requested"
          title="Open the request"
          onClick={() => onOpen?.()}
        />
      </div>
    );
  }
  // Total = the booking's stored total (the sheet's Amount) when present, else a
  // recompute. The "due" is the BALANCE — total minus what's been paid (deposit)
  // — i.e. the sheet's Due column, not the full total.
  // ONE decision for the label and the click — see deskPayAction. Computing the
  // figure twice is how the label came to promise a refund the click never made.
  const action = deskPayAction(b);
  const credit = action.act === 'credit';
  const isPaid = derivePaymentStatus(b) === 'paid';

  // OVERPAID is its own state, not a flavour of settled. This control used to
  // read `isPaid ? "Paid" : "<balance> due"`, which replaced the figure with a
  // word the instant a booking settled — so money collected OVER the total
  // vanished from the desk entirely, with nothing to tell staff the customer is
  // owed it back. Show the amount, and say which way it points.
  const payLabel = credit ? (
    <>
      <span className="amount">{fmtEuro(action.amount)}</span> <span className="pay-word">Credit</span>
    </>
  ) : isPaid ? (
    <>
      <Icon name="check" size={11} /> <span className="pay-word">Paid</span>
    </>
  ) : (
    <>
      <span className="amount">{fmtEuro(action.amount)}</span> <span className="pay-word">due</span>
    </>
  );

  return (
    <div className="desk-actions">
      <button
        type="button"
        // No click-flash keyframe (Freddi, 2026-07-24): the brightness pulse was
        // covering for a state change that took two round trips to appear. The
        // write is optimistic now, so the button changes on the press and a
        // plain token transition carries it — the booking modal's care-tile feel.
        className={'desk-row-pay' + (isPaid ? ' paid' : '') + (credit ? ' credit' : '')}
        onClick={(e) => {
          e.stopPropagation();
          onTogglePaid();
        }}
        // A credit is a READOUT, not a control: there is no desk action that
        // hands money back, and the press used to run the un-settle path — which
        // either did nothing or removed an entire settlement. Resolving an
        // overpayment is the booking modal's job, where the payments themselves
        // can be corrected.
        disabled={busyPaid || !action.actionable}
        aria-pressed={isPaid}
        title={credit
          ? `Overpaid by ${fmtEuro(action.amount)} — owed back to the customer. Correct the payments in the booking to resolve it.`
          : isPaid ? 'Click to mark unpaid' : `Mark ${fmtEuro(action.amount)} paid`}
      >
        {payLabel}
      </button>
      <StatusPill status={status} onClick={onAction} disabled={busyStatus} title="Click to advance status" className={done ? 'is-done' : undefined} />
    </div>
  );
}
